import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowUp, Clock } from '@phosphor-icons/react';
import { chatService } from '../../shared/services/chatService';
import { useAuth } from '../../shared/contexts/AuthContext';
import { useChatStore } from '../../shared/store/chatStore';
import { useUserStore } from '../../shared/store/userStore';
import { PageHeader } from '../../shared/components/layout/pageHeader';
import { Header } from '../../shared/components/layout/Header';
import { SpecialLine } from '../../shared/components/layout/SpecialLine';
import { format } from 'date-fns';

export default function Home() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { profile } = useUserStore();
    const [newMessage, setNewMessage] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    
    const { 
        isLoading,
        createChat,
        setInputValue,
        chats = []
    } = useChatStore();

    const recentChats = chats.slice(0, 3);

    const searchParams = new URLSearchParams(location.search);
    const shouldFocus = searchParams.get('focus') === 'true';

    // Focus the textarea on mount and when returning to home
    useEffect(() => {
        const focusTextarea = () => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.selectionStart = textareaRef.current.value.length;
            }
        };

        // Initial focus
        focusTextarea();

        // Focus when the window regains focus
        window.addEventListener('focus', focusTextarea);

        return () => {
            window.removeEventListener('focus', focusTextarea);
        };
    }, []);

    // Additional focus when navigating or when focus param is present
    useEffect(() => {
        if (shouldFocus || location.pathname === '/') {
            const timeoutId = setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.focus();
                    textareaRef.current.selectionStart = textareaRef.current.value.length;
                    if (shouldFocus) {
                        navigate('/', { replace: true });
                    }
                }
            }, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [location.key, shouldFocus, navigate]);

    async function handleNewChat(e: React.FormEvent) {
        e.preventDefault();
        const content = newMessage.trim();
        if (!content || isLoading || !user) return;

        try {
            sessionStorage.setItem('pendingMessage', content);
            const newChat = await createChat(content, user.id);
            if (newChat) {
                navigate(`/chat/${newChat.id}`);
            }
        } catch (error) {
            console.error('Error:', error);
            sessionStorage.removeItem('pendingMessage');
        }
    }

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'morning';
        if (hour < 18) return 'afternoon';
        return 'evening';
    };

    const getFirstName = () => {
        if (!profile) return '';
        return profile.first_name || '';
    };

    return (
        <div className="min-h-screen">
            <Header />
            <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
                <div className="w-full max-w-3xl px-4 py-8">
                    <div className="flex items-center justify-center mb-6">
                        <SpecialLine text="The most powerful Bible app" />
                    </div>
                    <PageHeader
                        headline="Never walk alone again"
                        subheadline="Get answers to life's toughest questions"
                    />

                    <form onSubmit={handleNewChat} className="mb-8 max-w-2xl mx-auto px-8">
                        <div className="relative">
                            <textarea
                                ref={textareaRef}
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleNewChat(e);
                                    }
                                }}
                                placeholder="What are you dealing with?"
                                rows={4}
                                className="w-full bg-white rounded-lg border border-[#E3DCD2] shadow-sm focus:border-[#FF5C3C] focus:ring-1 focus:ring-[#FF5C3C] focus:outline-none pr-14 p-4 placeholder:text-[rgba(54,30,6,0.5)]"
                                disabled={isLoading}
                                autoFocus
                            />
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="absolute top-4 right-4 inline-flex items-center p-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#361E06] hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                <ArrowUp className="w-5 h-5" />
                            </button>
                        </div>
                    </form>

                    {recentChats.length > 0 && (
                        <div className="max-w-xl mx-auto">
                            <div className="flex items-center gap-2 mb-4 text-[#361E06]">
                                <h2 className="font-medium text-xs pl-3">Recent Conversations</h2>
                            </div>
                            <div className="space-y-2">
                                {recentChats.map((chat) => (
                                    <button
                                        key={chat.id}
                                        onClick={() => navigate(`/chat/${chat.id}`)}
                                        className="bg-[#F9F7F4] w-full flex items-center justify-between p-3 rounded-lg hover:bg-[rgba(0,0,0,0.05)] transition-colors text-left"
                                    >
                                        <span className="text-[#361E06] font-medium truncate flex-1">
                                            {chat.title || chat.first_message || 'Untitled Chat'}
                                        </span>
                                        <span className="text-sm text-[#361E06]/60 ml-4">
                                            {format(new Date(chat.created_at), 'MMM d, yyyy')}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}